import {
  type ComponentProps,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * Legacy classes for hand-rolled menu rows. Prefer `ViewportContextMenuItem`, which is a real
 * Radix menu item (arrow-key navigation, typeahead, Enter/Space to select).
 */
export const VIEWPORT_CONTEXT_MENU_ITEM_CLASS =
  "flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-xs leading-tight hover:bg-muted";

const MENU_OFFSET_PX = 6;
const MENU_EDGE_PADDING_PX = 4;

interface ViewportContextMenuItemProps
  extends Omit<ComponentProps<typeof DropdownMenuItem>, "variant"> {
  icon?: ReactNode;
  destructive?: boolean;
  /** Ignored — items are menu items, not buttons. Accepted so older call sites keep compiling. */
  type?: "button" | "submit" | "reset";
}

export function ViewportContextMenuItem({
  icon,
  destructive = false,
  className,
  children,
  type: _type,
  ...props
}: ViewportContextMenuItemProps) {
  return (
    <DropdownMenuItem
      variant={destructive ? "destructive" : "default"}
      className={cn("gap-2 px-2 py-1 text-xs leading-tight", className)}
      {...props}
    >
      {icon}
      {children}
    </DropdownMenuItem>
  );
}

interface ViewportContextMenuProps extends HTMLAttributes<HTMLDivElement> {
  /** Pointer position relative to the nearest positioned ancestor (the viewport container). */
  x: number;
  y: number;
  children: ReactNode;
  /**
   * Called when the menu asks to close: Escape, a click/focus outside, or an item selection.
   * Callers that already unmount the menu from their own handlers may omit it.
   */
  onClose?: () => void;
}

/**
 * Right-click menu for canvas viewports, anchored at `(x, y)`. Built on Radix DropdownMenu with a
 * zero-size anchor, so it gets roving focus, typeahead, Escape, and collision-aware placement.
 * Non-modal: the viewport behind stays interactive while the menu is open.
 */
export function ViewportContextMenu({
  x,
  y,
  children,
  className,
  onMouseDown,
  onClose,
  ...props
}: ViewportContextMenuProps) {
  return (
    // Re-key on position so a second right-click re-anchors instead of keeping the old placement.
    <DropdownMenu
      key={`${x}:${y}`}
      open
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          onClose?.();
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <span
          aria-hidden="true"
          tabIndex={-1}
          data-slot="viewport-context-menu-anchor"
          className="pointer-events-none absolute size-0"
          style={{ left: x, top: y }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        sideOffset={MENU_OFFSET_PX}
        alignOffset={MENU_OFFSET_PX}
        collisionPadding={MENU_EDGE_PADDING_PX}
        onCloseAutoFocus={(event) => event.preventDefault()}
        className={cn(
          "min-w-28 max-w-44 bg-background/95 p-0.5 shadow-xl backdrop-blur",
          className
        )}
        onMouseDown={(event) => {
          // Keep canvas mousedown handlers (selection, pan) from seeing menu clicks; the content
          // is portaled but React events still bubble through the component tree.
          event.stopPropagation();
          onMouseDown?.(event);
        }}
        {...props}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
